import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { PrincipalTypeSelectors } from "../../../../components/guardian/PrincipalFormComponents";
import { getPrincipalMode } from "../../../../components/guardian/principalHelpers";
import { HISTORIC_GUARDIAN_CONFIG } from "../historicGuardianConfig";
import { useHistoricGuardianDropdowns } from "../hooks/useHistoricGuardianDropdowns";
import { useHistoricGuardianHandlers } from "../hooks/useHistoricGuardianHandlers";
import HistoricAttorneySection from "./sections/HistoricAttorneySection";
import HistoricManualEntrySection from "./sections/HistoricManualEntrySection";
import HistoricPgSection from "./sections/HistoricPgSection";

/**
 * Shared historic guardian principals step (bond creation historic).
 *
 * @param {object} props
 * @param {string} props.bondKind - historicGuardianConfig bond kind key
 * @param {React.ComponentType<any>} props.GoogleAutoComplete
 * @param {React.ComponentType<any>} props.LabeledCreatableSelect
 * @param {() => void} props.scrollToTop
 */
export default function HistoricGuardianFormBase({
  bondKind,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  scrollToTop,
  data = { guardians: [] },
  updateData,
  globalData,
  errors = {},
}) {
  const cfg = HISTORIC_GUARDIAN_CONFIG[bondKind];
  const keys = cfg.fieldKeys;
  const ms = cfg.manualStyles;

  const { role } = useSelector((state) => state.auth);
  const pgId = useSelector((state) => state.pg.pgId);
  const lawfirm = useSelector((state) => state.lawfirm);

  const {
    allCities,
    allStates,
    professionalGuardians,
    attorneys,
    loadingCitiesStates,
    loadingGuardians,
    loadingAttorneys,
  } = useHistoricGuardianDropdowns({
    historicCfg: cfg,
    role,
    lawfirm,
    scrollToTop,
  });

  const {
    emailExists,
    emailChecking,
    emailDialogOpen,
    getDebouncedCheckEmail,
    handleDialogClose,
    handleDialogContinue,
    handleDialogCancel,
    fetchProfessionalGuardianDetails,
    handleAttorneyHomeAddressChange,
    handleAttorneySelection,
    handleGoogleCallback,
    handleZipCodeChange,
    formatSSN,
    updateGuardianAt,
    handleProfessionalGuardianChange,
    handleAttorneyChange,
    showHighBondSsn,
    showManualOccupationField,
    showRelationshipField,
  } = useHistoricGuardianHandlers({
    bondKind,
    cfg,
    keys,
    data,
    updateData,
    globalData,
    role,
    pgId,
    professionalGuardians,
  });

  const onGuardianEmailCommit = useCallback(
    (index, guardiansSnap, email, debouncedFn) => {
      const updatedGuardians = [...guardiansSnap];
      updatedGuardians[index] = {
        ...updatedGuardians[index],
        [keys.email]: email,
      };
      updateData({ ...data, guardians: updatedGuardians });
      if (debouncedFn) debouncedFn(email);
    },
    [data, keys.email, updateData]
  );

  const renderSinglePrincipal = (index, guardian, guardians) => {
    const mode = getPrincipalMode(guardian);

    return (
      <Box key={`guardian${index}`} className="grid grid-cols-1 gap-4">
        <Typography variant="h6" className="font-semibold mb-2">
          Principal #{index + 1}
        </Typography>

        <PrincipalTypeSelectors
          guardian={guardian}
          isDisabled={false}
          isGuest={false}
          onProfessionalGuardianChange={(value) =>
            handleProfessionalGuardianChange(index, value, guardian, guardians)
          }
          onAttorneyChange={(value) =>
            handleAttorneyChange(index, value, guardians)
          }
        />

        {mode === "pg_lookup" && (
          <HistoricPgSection
            index={index}
            guardian={guardian}
            guardians={guardians}
            cfg={cfg}
            professionalGuardians={professionalGuardians}
            loadingGuardians={loadingGuardians}
            updateGuardianAt={updateGuardianAt}
            fetchProfessionalGuardianDetails={fetchProfessionalGuardianDetails}
          />
        )}

        {mode === "attorney_lookup" && (
          <HistoricAttorneySection
            index={index}
            guardian={guardian}
            guardians={guardians}
            cfg={cfg}
            keys={keys}
            attorneys={attorneys}
            loadingAttorneys={loadingAttorneys}
            errors={errors}
            data={data}
            updateData={updateData}
            GoogleAutoComplete={GoogleAutoComplete}
            LabeledCreatableSelect={LabeledCreatableSelect}
            allCities={allCities}
            allStates={allStates}
            loadingCitiesStates={loadingCitiesStates}
            handleAttorneyHomeAddressChange={handleAttorneyHomeAddressChange}
            handleAttorneySelection={handleAttorneySelection}
            handleGoogleCallback={handleGoogleCallback}
            handleZipCodeChange={handleZipCodeChange}
          />
        )}

        {mode === "manual" && (
          <HistoricManualEntrySection
            bondKind={bondKind}
            index={index}
            guardian={guardian}
            guardians={guardians}
            cfg={cfg}
            keys={keys}
            ms={ms}
            data={data}
            updateData={updateData}
            GoogleAutoComplete={GoogleAutoComplete}
            LabeledCreatableSelect={LabeledCreatableSelect}
            allCities={allCities}
            allStates={allStates}
            loadingCitiesStates={loadingCitiesStates}
            handleGoogleCallback={handleGoogleCallback}
            handleZipCodeChange={handleZipCodeChange}
            showHighBondSsn={showHighBondSsn}
            showManualOccupationField={showManualOccupationField}
            showRelationshipField={showRelationshipField}
            formatSSN={formatSSN}
            emailExists={emailExists}
            emailChecking={emailChecking}
            emailDialogOpen={emailDialogOpen}
            getDebouncedCheckEmail={getDebouncedCheckEmail}
            handleDialogClose={handleDialogClose}
            handleDialogContinue={handleDialogContinue}
            handleDialogCancel={handleDialogCancel}
            onGuardianEmailCommit={onGuardianEmailCommit}
          />
        )}
      </Box>
    );
  };

  const renderGuardianFields = () => {
    const numberOfGuardians =
      globalData.basicInformation?.[cfg.guardianCountKey] || 1;
    const guardians = Array.isArray(data.guardians) ? data.guardians : [];
    return Array.from({ length: numberOfGuardians }, (_, index) =>
      renderSinglePrincipal(index, guardians[index] || {}, guardians)
    );
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Principals
      </Typography>
      {renderGuardianFields()}
    </Box>
  );
}

HistoricGuardianFormBase.propTypes = {
  bondKind: PropTypes.string.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  scrollToTop: PropTypes.func.isRequired,
  data: PropTypes.shape({ guardians: PropTypes.array }),
  updateData: PropTypes.func.isRequired,
  globalData: PropTypes.object.isRequired,
  errors: PropTypes.object,
};
